import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ORDENABLE_PAGE_SIZE, REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useListService from '@/data/hook/useListService';
import { resourceQueries } from '@/data/query-keys/resourceQueryKey';
import {
  createLessonResource,
  removeLessonResource,
  reorderLessonResources,
  updateLessonResource,
} from '@/data/services/resourceServices';
import Resource, { ResourceDTO } from '@/models/Resource';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { reorder } from '@/utils/list';
import { OnUploadProgressFn } from '@/utils/shared/uploadProgress';
import { useEditableLesson } from '../lesson/useEditableLesson';

type UseResourcesProps = {
  lessonId: number;
};

type CreateMutationArgs = {
  body: Partial<ResourceDTO>;
  onUploadProgress?: OnUploadProgressFn;
};

type UpdateMutationArgs = {
  resourceId: number;
  data: Partial<ResourceDTO>;
  onUploadProgress?: OnUploadProgressFn;
};

const useResources = ({ lessonId }: UseResourcesProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.resourcesTab',
  });

  const {
    results: resources,
    isInitialLoading,
    cacheManager,
  } = useListService<Resource>({
    ...resourceQueries.lessonList(lessonId, { pageSize: ORDENABLE_PAGE_SIZE }),
    enabled: !!lessonId,
    staleTime: REQUEST_STALE_TIME_IN_MS,
    keepPreviousData: true,
  });

  const { runEditable } = useEditableLesson(lessonId);

  const createMutation = useMutation({
    mutationFn: ({ body, onUploadProgress }: CreateMutationArgs) =>
      runEditable(editableLessonId =>
        createLessonResource(editableLessonId, body, { onUploadProgress }),
      ),
    onSuccess: resource => {
      cacheManager.add(resource);
      alert.success(t('createSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ resourceId, data, onUploadProgress }: UpdateMutationArgs) =>
      runEditable(editableLessonId =>
        updateLessonResource(editableLessonId, resourceId, data, {
          onUploadProgress,
        }),
      ),
    onSuccess: (resource, { resourceId }) => {
      cacheManager.update(
        { ...resource, id: resourceId },
        { newId: resource.id },
      );
      alert.success(t('updateSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (resourceId: number) =>
      runEditable(editableLessonId =>
        removeLessonResource(editableLessonId, resourceId),
      ),
    onSuccess: (_, resourceId) => {
      cacheManager.remove(resourceId);
      alert.success(t('deleteSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const reorderMutation = useMutation({
    onMutate: (resourceIds: number[]) => {
      const reordenedResources = reorder(resources, resourceIds);
      cacheManager.set(reordenedResources);
    },
    mutationFn: (resourceIds: number[]) =>
      runEditable(editableLessonId =>
        reorderLessonResources(
          editableLessonId,
          resourceIds.map(id => ({ id })),
        ),
      ),
    onSuccess: () => alert.success(t('reorderSuccess')),
    onError: err => {
      cacheManager.undo();
      alert.error(getErrorMessage(err));
    },
  });

  return {
    resources,
    isLoading: isInitialLoading,
    createResource: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateResource: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteResource: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
    reorderResources: reorderMutation.mutate,
    isReordering: reorderMutation.isPending,
  };
};

export default useResources;
